import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, rolesApi } from '../../utils/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Admin.css';

const EMPTY_FORM = { username: '', fullName: '', email: '', password: '', isActive: true, mustChangePwd: true, roleIds: [] };

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ── Confirm dialog ─────────────────────────────────────────────────── */
function ConfirmModal({ message, onConfirm, onCancel, danger }) {
  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal" style={{ maxWidth: 400 }}>
        <div className="adm-modal-head"><h2>Confirm</h2></div>
        <div className="adm-modal-body adm-confirm-body">{message}</div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className={`adm-btn ${danger ? 'adm-btn--danger' : 'adm-btn--primary'}`} onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

/* ── Reset-password modal ─────────────────────────────────────────────── */
function ResetPasswordModal({ user, onClose }) {
  const [pwd, setPwd] = useState('');
  const [mustChange, setMustChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!pwd || pwd.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setSaving(true); setError(null);
    try {
      await usersApi.resetPassword(user.id, { newPassword: pwd, mustChangePwd: mustChange });
      onClose(true);
    } catch (e) { setError(e.message || 'Failed to reset password'); }
    finally { setSaving(false); }
  };

  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal" style={{ maxWidth: 400 }}>
        <div className="adm-modal-head">
          <h2>Reset Password — {user.username}</h2>
          <button className="adm-modal-close" onClick={() => onClose(false)}><i className="fas fa-times"></i></button>
        </div>
        <div className="adm-modal-body">
          {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
          <div className="adm-field">
            <label className="adm-label">New Password</label>
            <input className="adm-input" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
              placeholder="Min 8 characters" autoFocus />
          </div>
          <label className="adm-perm-item" style={{ marginTop: '0.5rem' }}>
            <input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} />
            Force user to change password on next login
          </label>
        </div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={() => onClose(false)}>Cancel</button>
          <button className="adm-btn adm-btn--primary" onClick={submit} disabled={saving}>
            {saving ? <LoadingSpinner size="small" variant="button" /> : 'Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Create / Edit user modal ─────────────────────────────────────────── */
function UserModal({ user, allRoles, onClose }) {
  const isEdit = !!user;
  const [form, setForm] = useState(
    isEdit
      ? { fullName: user.fullName || '', email: user.email || '', isActive: user.isActive, roleIds: (user.roles || []).map((r) => r.id) }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleRole = (id) =>
    setForm((f) => ({
      ...f,
      roleIds: f.roleIds.includes(id) ? f.roleIds.filter((x) => x !== id) : [...f.roleIds, id],
    }));

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        await usersApi.update(user.id, { fullName: form.fullName, email: form.email || null, isActive: form.isActive, roleIds: form.roleIds });
      } else {
        await usersApi.create(form);
      }
      onClose(true);
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal">
        <div className="adm-modal-head">
          <h2>{isEdit ? `Edit — ${user.username}` : 'Create User'}</h2>
          <button className="adm-modal-close" onClick={() => onClose(false)}><i className="fas fa-times"></i></button>
        </div>
        <div className="adm-modal-body">
          {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
          {!isEdit && (
            <>
              <div className="adm-field">
                <label className="adm-label">Username *</label>
                <input className="adm-input" value={form.username} onChange={(e) => set('username', e.target.value)}
                  placeholder="e.g. j.smith" autoFocus />
                <p className="adm-field-hint">Letters, numbers, dot, dash, underscore. Min 3 chars.</p>
              </div>
              <div className="adm-field">
                <label className="adm-label">Password *</label>
                <input className="adm-input" type="password" value={form.password} onChange={(e) => set('password', e.target.value)}
                  placeholder="Min 8 characters" />
              </div>
              <label className="adm-perm-item" style={{ marginBottom: '1rem' }}>
                <input type="checkbox" checked={form.mustChangePwd} onChange={(e) => set('mustChangePwd', e.target.checked)} />
                Force password change on first login
              </label>
            </>
          )}
          <div className="adm-field">
            <label className="adm-label">Full Name *</label>
            <input className="adm-input" value={form.fullName} onChange={(e) => set('fullName', e.target.value)}
              placeholder="e.g. John Smith" autoFocus={isEdit} />
          </div>
          <div className="adm-field">
            <label className="adm-label">Email</label>
            <input className="adm-input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
              placeholder="optional" />
          </div>
          {isEdit && (
            <div className="adm-field">
              <label className="adm-label">Status</label>
              <select className="adm-input" value={form.isActive ? 'true' : 'false'}
                onChange={(e) => set('isActive', e.target.value === 'true')}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          )}
          {allRoles.length > 0 && (
            <div className="adm-field">
              <label className="adm-label">Roles</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {allRoles.map((r) => (
                  <label key={r.id} className="adm-perm-item"
                    style={{ background: form.roleIds.includes(r.id) ? 'var(--primary-50)' : 'var(--surface-3)',
                      padding: '0.3rem 0.7rem', borderRadius: 'var(--border-radius-pill)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={() => onClose(false)}>Cancel</button>
          <button className="adm-btn adm-btn--primary" onClick={submit} disabled={saving}>
            {saving ? <LoadingSpinner size="small" variant="button" /> : (isEdit ? 'Save Changes' : 'Create User')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────────────── */
const UsersPage = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('users.manage');

  const [users, setUsers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('');

  const [modal, setModal] = useState(null);  // null | { type: 'create'|'edit'|'reset'|'deactivate', user? }

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterActive !== '') params.isActive = filterActive;
      const [usersData, rolesData] = await Promise.all([usersApi.list(params), rolesApi.list()]);
      setUsers(usersData.data || []);
      setAllRoles(rolesData);
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [search, filterActive]);

  useEffect(() => { load(); }, [load]);

  const closeModal = (refresh) => {
    setModal(null);
    if (refresh) load();
  };

  const doDeactivate = async () => {
    try {
      await usersApi.deactivate(modal.user.id);
      closeModal(true);
    } catch (e) { setError(e.message || 'Failed to deactivate'); setModal(null); }
  };

  return (
    <div className="adm-page">
      <div className="adm-header">
        <div>
          <h1 className="adm-title"><i className="fas fa-users-cog" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Users</h1>
          <p className="adm-subtitle">Manage system accounts and role assignments.</p>
        </div>
        {canManage && (
          <button className="adm-btn adm-btn--primary" onClick={() => setModal({ type: 'create' })}>
            <i className="fas fa-plus"></i> New User
          </button>
        )}
      </div>

      <div className="adm-toolbar">
        <input className="adm-search" placeholder="Search by name or username…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()} />
        <select className="adm-select" value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
          <option value="">All status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <button className="adm-btn adm-btn--ghost" onClick={load}>
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>

      {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      <div className="adm-card">
        {loading ? (
          <LoadingSpinner variant="default" message="Loading users…" />
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Created</th>
                  {canManage && <th style={{ textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={canManage ? 7 : 6} className="adm-empty">No users found.</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.id}>
                    <td><strong style={{ fontFamily: 'var(--font-family-mono)', fontSize: '0.85rem' }}>{u.username}</strong></td>
                    <td>{u.fullName}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{u.email || '—'}</td>
                    <td>
                      {(u.roles || []).length === 0
                        ? <span style={{ color: 'var(--text-faint)' }}>none</span>
                        : (u.roles || []).map((r) => (
                          <span key={r.id} className="adm-badge adm-badge--system" style={{ marginRight: 4 }}>{r.name}</span>
                        ))}
                    </td>
                    <td>
                      <span className={`adm-badge ${u.isActive ? 'adm-badge--active' : 'adm-badge--inactive'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{fmtDate(u.createdAt)}</td>
                    {canManage && (
                      <td>
                        <div className="adm-actions">
                          <button className="adm-btn adm-btn--ghost adm-btn--sm" title="Edit"
                            onClick={() => setModal({ type: 'edit', user: u })}>
                            <i className="fas fa-pen"></i>
                          </button>
                          <button className="adm-btn adm-btn--ghost adm-btn--sm" title="Reset Password"
                            onClick={() => setModal({ type: 'reset', user: u })}>
                            <i className="fas fa-key"></i>
                          </button>
                          {u.isActive && (
                            <button className="adm-btn adm-btn--danger adm-btn--sm" title="Deactivate"
                              onClick={() => setModal({ type: 'deactivate', user: u })}>
                              <i className="fas fa-user-slash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal?.type === 'create' && <UserModal allRoles={allRoles} onClose={closeModal} />}
      {modal?.type === 'edit'   && <UserModal user={modal.user} allRoles={allRoles} onClose={closeModal} />}
      {modal?.type === 'reset'  && <ResetPasswordModal user={modal.user} onClose={closeModal} />}
      {modal?.type === 'deactivate' && (
        <ConfirmModal
          danger
          message={<>Deactivate user <strong>{modal.user.username}</strong>? They will no longer be able to log in.</>}
          onConfirm={doDeactivate}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default UsersPage;
